// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../App';
import { motion } from 'framer-motion';
import { LevelBadge, ProgressBar } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { CheckCircle, Clock, Lock, ChevronRight, Award, Star } from 'lucide-react';
import CertificateModal from '../components/CertificateModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEVELS = [
  { level: 1, name: 'Base', description: 'Concetti di denaro, risparmio, conto corrente', icon: '🌱' },
  { level: 2, name: 'Intermedio', description: 'Investimenti, ETF, obbligazioni', icon: '📈' },
  { level: 3, name: 'Avanzato', description: 'Portafoglio, asset allocation, rischio', icon: '💼', premium: true },
  { level: 4, name: 'Esperto', description: 'Fiscalità, pianificazione, previdenza', icon: '🎓', premium: true },
];

const LEVEL_COLORS = { 1: '#3D7A52', 2: '#5B8DB8', 3: '#C9A84C', 4: '#E87040' };

const PercorsiPage = () => {
  const { user } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [certModal, setCertModal] = useState({ open: false, level: null });
  const [certificates, setCertificates] = useState([]);
  const [loginPrompt, setLoginPrompt] = useState(false);

  // P0 Fix: Lessons always fetched on mount, independent of auth state
  // This ensures guests can see lesson list without login
  useEffect(() => {
    const fetchLessons = async () => {
      try {
        const res = await fetch(`${API_URL}/api/lessons`);
        if (res.ok) setLessons(await res.json());
      } catch (error) {
        console.error('Error fetching lessons:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLessons();
  }, []); // Run once on mount — independent of auth state

  // Separately fetch certificates when user is authenticated
  useEffect(() => {
    if (!user) return;
    const fetchCerts = async () => {
      try {
        const cRes = await fetch(`${API_URL}/api/certificates`, { credentials: 'include' });
        if (cRes.ok) setCertificates(await cRes.json());
      } catch {}
    };
    fetchCerts();
  }, [user]);

  // ── helpers ──────────────────────────────────────────────────────────────
  const quizScores = user?.quiz_scores || {};

  const getRegularLessons = (level) =>
    lessons.filter(l => l.level === level && !l.is_level_quiz).sort((a, b) => a.order - b.order);

  const getLevelQuiz = (level) =>
    lessons.find(l => l.level === level && l.is_level_quiz);

  const isAllRegularComplete = (level) => {
    const reg = getRegularLessons(level);
    return reg.length > 0 && reg.every(l => l.completed);
  };

  const isQuizPassed = (level) => {
    const q = getLevelQuiz(level);
    if (!q) return true; // no quiz = unlocked
    const score = quizScores[q.lesson_id];
    return typeof score === 'number' && score >= 60;
  };

  /** Level N unlocked if: level 1, or previous level quiz passed */
  const isLevelUnlocked = (level) => {
    if (level === 1) return true;
    if (user?.tier !== 'premium' && level > 2) return false; // premium gate still applies
    return isQuizPassed(level - 1);
  };

  const getLevelProgress = (level) => {
    const reg = getRegularLessons(level);
    if (reg.length === 0) return 0;
    return Math.round(reg.filter(l => l.completed).length / reg.length * 100);
  };

  const isLevelComplete = (level) => isAllRegularComplete(level) && isQuizPassed(level);
  const getCert = (level) => certificates.find(c => c.level === level);
  const isPremiumLocked = (level) => level > 2 && user?.tier !== 'premium';

  const handleOpenCert = async (level, levelName) => {
    if (!getCert(level)) {
      try {
        const res = await fetch(`${API_URL}/api/certificates`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ level, level_name: `Livello ${levelName}` }),
        });
        if (res.ok) {
          const cert = await res.json();
          setCertificates(prev => [...prev.filter(c => c.level !== level), cert]);
        }
      } catch {}
    }
    setCertModal({ open: true, level, levelName });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C9A84C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1C1C1A]">
      <Helmet>
        <title>Percorsi di Apprendimento — SAPIENS</title>
        <meta name="description" content="16 lezioni su 4 livelli: dalle basi della finanza personale fino alla pianificazione previdenziale e protezione patrimoniale. Gratis." />
        <link rel="canonical" href="https://finsapiens.it/percorsi" />
      </Helmet>
      <div className="container-wide py-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="text-4xl font-semibold text-[#E8E8E4] mb-2">Percorsi</h1>
          <p className="text-[#888780]">Completa le lezioni, supera i quiz e sblocca il livello successivo.</p>
        </motion.div>

        {/* Level Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" style={{ alignItems: 'stretch' }} data-testid="percorsi-grid">
          {LEVELS.map((levelInfo, idx) => {
            const unlocked = isLevelUnlocked(levelInfo.level);
            const premLocked = isPremiumLocked(levelInfo.level);
            const progress = getLevelProgress(levelInfo.level);
            const complete = isLevelComplete(levelInfo.level);
            const quizPassed = isQuizPassed(levelInfo.level);
            const cert = getCert(levelInfo.level);
            const regCount = getRegularLessons(levelInfo.level).length;
            const color = LEVEL_COLORS[levelInfo.level];
            const isSelected = selectedLevel === levelInfo.level;

            return (
              <motion.div key={levelInfo.level} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.08 }}>
                <div
                  className={`relative sapiens-card p-6 transition-all cursor-pointer card-hover h-full ${isSelected ? '' : ''}`}
                  style={{ height: '100%' }}
                  style={{ borderColor: isSelected ? color : undefined }}
                  onClick={() => setSelectedLevel(isSelected ? null : levelInfo.level)}
                  data-testid={`level-card-${levelInfo.level}`}
                >
                  {/* Lock icon */}
                  {(!unlocked) && (
                    <div className="absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.06)' }}>
                      <Lock className="w-3.5 h-3.5 text-[#888780]" />
                    </div>
                  )}
                  {complete && unlocked && (
                    <div className="absolute top-3 right-3">
                      <CheckCircle className="w-5 h-5" style={{ color }} />
                    </div>
                  )}

                  <div className="text-2xl mb-3">{levelInfo.icon}</div>
                  <LevelBadge level={levelInfo.level} />
                  <h2 className="text-lg font-medium text-[#E8E8E4] mt-2">{levelInfo.name}</h2>
                  <p className="text-sm text-[#888780] mt-1 leading-snug">{levelInfo.description}</p>
                  <p className="text-xs text-[#888780] mt-2">{regCount} lezioni + quiz</p>

                  {unlocked && (
                    <div className="mt-4">
                      <ProgressBar value={progress} showLabel size="small" />
                    </div>
                  )}

                  {/* Quiz passed badge */}
                  {quizPassed && unlocked && regCount > 0 && (
                    <div className="mt-2 text-xs font-medium flex items-center gap-1" style={{ color }}>
                      <Star className="w-3 h-3" />Quiz superato
                    </div>
                  )}

                  {/* Locked by quiz: show why */}
                  {!unlocked && !premLocked && levelInfo.level > 1 && (
                    <p className="text-xs text-[#888780] mt-3">Supera il quiz Livello {levelInfo.level - 1} per sbloccare</p>
                  )}

                  {/* Premium lock */}
                  {premLocked && (
                    <div className="mt-3 p-3 rounded-lg" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)' }}>
                      <div className="text-xs font-medium text-[#E8E8E4] mb-1">Livello {levelInfo.level} — Contenuto Premium</div>
                      <div className="text-xs text-[#888780] mb-2">Accedi a SAPIENS Premium</div>
                      <div className="text-xs font-bold text-[#C9A84C]">€4,99/mese · Prezzo Fondatori</div>
                      <div className="text-[10px] text-[#888780]">Disponibile fino al 31 agosto 2026</div>
                    </div>
                  )}

                  {/* Cert button */}
                  {complete && !premLocked && (
                    <button onClick={e => { e.stopPropagation(); handleOpenCert(levelInfo.level, levelInfo.name); }}
                      className="mt-3 flex items-center gap-2 text-sm font-medium hover:opacity-80 transition-opacity"
                      style={{ color }} data-testid={`cert-btn-level-${levelInfo.level}`}>
                      <Award className="w-4 h-4" />
                      {cert ? 'Visualizza certificato' : 'Ottieni certificato'}
                    </button>
                  )}
                  {premLocked && user && (
                    <button onClick={e => { e.stopPropagation(); handleOpenCert(levelInfo.level, levelInfo.name); }}
                      className="mt-3 flex items-center gap-2 text-sm font-medium text-[#888780] hover:text-[#C9A84C] transition-colors"
                      data-testid={`cert-preview-level-${levelInfo.level}`}>
                      <Award className="w-4 h-4" />Anteprima certificato Premium
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Lessons for selected level */}
        {selectedLevel && (() => {
          const regular = getRegularLessons(selectedLevel);
          const quizLesson = getLevelQuiz(selectedLevel);
          const allRegDone = isAllRegularComplete(selectedLevel);
          const qPassed = isQuizPassed(selectedLevel);
          const quizScore = quizLesson ? quizScores[quizLesson.lesson_id] : null;
          const color = LEVEL_COLORS[selectedLevel];

          return (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="sapiens-card p-6 mb-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-medium text-[#E8E8E4]">
                  Livello {selectedLevel} — {LEVELS[selectedLevel - 1].name}
                </h2>
                <Button variant="ghost" onClick={() => setSelectedLevel(null)} className="text-[#888780] hover:text-[#E8E8E4]">Chiudi</Button>
              </div>

              <div className="space-y-3">
                {/* Regular lessons */}
                {regular.map((lesson, index) => {
                  const lessonContent = (
                    <div className="flex items-center justify-between p-4 rounded-xl border transition-colors group cursor-pointer"
                      style={{ background: '#1C1C1A', borderColor: lesson.completed ? `${color}40` : 'rgba(255,255,255,0.06)' }}
                      data-testid={`lesson-item-${lesson.lesson_id}`}>
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium"
                          style={{ background: lesson.completed ? color : '#252523', color: lesson.completed ? '#fff' : '#888780' }}>
                          {lesson.completed ? <CheckCircle className="w-5 h-5" /> : index + 1}
                        </div>
                        <div>
                          <h3 className="text-[#E8E8E4] font-medium">{lesson.title}</h3>
                          <div className="flex items-center gap-3 text-sm text-[#888780] mt-0.5">
                            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{lesson.estimated_time} min</span>
                            {lesson.completed && <span style={{ color }}>Completata</span>}
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-[#888780] group-hover:text-[#C9A84C] transition-colors" />
                    </div>
                  );

                  if (!user) {
                    return (
                      <div key={lesson.lesson_id} onClick={() => setLoginPrompt(true)}>
                        {lessonContent}
                      </div>
                    );
                  }
                  return (
                    <Link key={lesson.lesson_id} to={`/lezione/${lesson.lesson_id}`}>
                      {lessonContent}
                    </Link>
                  );
                })}

                {/* Level Quiz card */}
                {quizLesson && (
                  <div className={`relative rounded-xl border p-4 transition-all ${allRegDone ? '' : 'opacity-50'}`}
                    style={{ background: qPassed ? `${color}10` : allRegDone ? 'rgba(201,168,76,0.06)' : '#1C1C1A', borderColor: qPassed ? `${color}60` : 'rgba(201,168,76,0.3)' }}
                    data-testid={`quiz-card-level-${selectedLevel}`}>
                    {!allRegDone && (
                      <div className="absolute top-3 right-3"><Lock className="w-4 h-4 text-[#888780]" /></div>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ background: qPassed ? color : allRegDone ? 'rgba(201,168,76,0.2)' : '#252523' }}>
                          {qPassed ? <CheckCircle className="w-5 h-5 text-white" /> : <Star className="w-5 h-5" style={{ color: allRegDone ? '#C9A84C' : '#888780' }} />}
                        </div>
                        <div>
                          <h3 className="font-semibold text-[#E8E8E4]">{quizLesson.title}</h3>
                          <p className="text-sm text-[#888780] mt-0.5">
                            {!allRegDone
                              ? `Completa tutte le ${regular.length} lezioni per sbloccare`
                              : qPassed
                              ? `Superato! Punteggio: ${quizScore}% — Livello ${selectedLevel + 1 <= 4 ? selectedLevel + 1 : ''} sbloccato`
                              : `Rispondi correttamente al 60% delle domande per sbloccare il prossimo livello`}
                          </p>
                          <div className="flex items-center gap-3 text-sm text-[#888780] mt-0.5">
                            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />5 min</span>
                            <span>5 domande</span>
                          </div>
                        </div>
                      </div>
                      {allRegDone && (
                        <Link to={`/lezione/${quizLesson.lesson_id}`}
                          className="shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all"
                          style={{ background: qPassed ? `${color}20` : '#C9A84C', color: qPassed ? color : '#3A2800' }}
                          data-testid={`quiz-start-level-${selectedLevel}`}>
                          {qPassed ? 'Rifai quiz' : 'Inizia quiz'}
                        </Link>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          );
        })()}

        {!selectedLevel && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12 text-[#888780]">
            <p className="text-lg">Seleziona un livello per vedere le lezioni disponibili</p>
            {!user && <p className="text-sm mt-2 text-[#555553]">Accedi per tracciare i tuoi progressi e sbloccare i quiz</p>}
          </motion.div>
        )}
      </div>

      <CertificateModal
        isOpen={certModal.open}
        onClose={() => setCertModal({ open: false, level: null })}
        level={certModal.level}
        levelName={certModal.levelName}
        userName={user?.name}
        issuedAt={getCert(certModal.level)?.issued_at}
        verificationCode={getCert(certModal.level)?.verification_code}
        onDownload={() => {}}
      />

      {/* Login prompt modal for non-logged users */}
      {loginPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="sapiens-card p-8 max-w-md w-full text-center">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: 'rgba(61,122,82,0.15)', border: '1px solid rgba(61,122,82,0.3)' }}>
              <Award className="w-7 h-7 text-[#3D7A52]" />
            </div>
            <h3 className="text-xl font-semibold text-[#E8E8E4] mb-3">Accedi per seguire questa lezione</h3>
            <p className="text-[#888780] text-sm mb-6 leading-relaxed">Crea un account gratuito per seguire le lezioni, tracciare i tuoi progressi e ottenere i certificati di completamento.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/login" className="sapiens-btn-primary flex-1 text-center py-2.5" data-testid="login-prompt-accedi-btn">Accedi</Link>
              <Link to="/login" className="sapiens-btn-secondary flex-1 text-center py-2.5" data-testid="login-prompt-register-btn">Registrati gratis</Link>
            </div>
            <button onClick={() => setLoginPrompt(false)} className="mt-4 text-sm text-[#888780] hover:text-[#E8E8E4]">Chiudi</button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default PercorsiPage;
